import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  BodyText,
  Box,
  Button,
  Card,
  Column,
  Heading,
  Row,
  SubscriptionHeader,
  TagColor,
} from '@chargify/custom-ui-components';
import { useDeviceInfo } from '@chargify/device-provider';

import { SubscriptionDetails } from '../SubscriptionDetails';
import {
  FormattedSubscriptionDetails,
  useComponentSettings,
  useServices,
} from '../../hooks';

import { ComponentsContainer } from '../../containers/ComponentsContainer';
import BillingDetailsContainer from '../../containers/BilingDetailsContainer/BilingDetailsContainer';
import { SubscriptionStatus } from '../../models';

import {
  FooterContentWrapper,
  RowWrapper,
  SubscriptionItem,
  ToggleWrapper,
} from './Subscription.styles';

import { bodyMotion } from './motion';

import { SubscriptionLayout } from './types';
import { SubscriptionBilling } from '../../models/subscription/types';
import Notification from '../SubscriptionDetails/components/Notification';
import CustomFieldsContainer from '../../containers/CustomFieldsContainer';
import { SubscriptionActions } from '../../containers/SubscriptionContainer/hooks/useSubscriptionAvailableActions';
import SubscriptionReferalCode from '../SubscriptionReferalCode';
import CouponsContainer from '../../containers/CouponsContainer';
import { Currency } from '@chargify/formatters';
import { Coupon } from '../../models/subscription/types';
import { SubscriptionDetailsMachineInterpreter } from '../../machines/subscription/types';
import { Link } from 'react-router-dom';

type SubscriptionProps = {
  /**
   * Subscription identifier
   */
  id: number;
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Subscription name
   */
  name: string;
  /**
   * Subscription status tag
   */
  statusTag?: { label: string; color: TagColor };
  /**
   * Total subscription price
   */
  price?: { label: string; color: string };
  /**
   * Card background color
   */
  cardColor?: 'white' | 'gray';
  /**
   * Product description
   */
  description: string;
  /**
   * Current subscription lifecycle state
   */
  status: SubscriptionStatus;
  /**
   * Subscription details preview event handler
   */
  onShowDetails: () => void;
  /**
   * Component layout
   */
  layout?: SubscriptionLayout;
  /**
   * Determines whether card is expanded and details are visible
   */
  detailsVisible?: boolean;
  /**
   *
   * Billing details
   * */
  billingDetails: SubscriptionBilling;
  /**
   * Subscription currency
   */
  currency: Currency;
  /**
   * Determines whether components section should be rendered
   */
  renderComponentsSection?: boolean;
  /**
   * Determines whether custom fields section should be rendered
   */
  renderCustomFieldsSection?: boolean;
  /**
   * Available subscription actions
   */
  availableActions?: SubscriptionActions[];
  /**
   * Subscription referal code
   */
  referralCode?: string;
  /**
   * 3DS action link
   */
  threeDsActionLink?: string;
  /**
   * Coupons
   */
  coupons: Coupon[];
  subscriptionDetailsService: SubscriptionDetailsMachineInterpreter;
} & FormattedSubscriptionDetails;

const Subscription = ({
  id,
  catalogId,
  name,
  price,
  statusTag,
  description,
  status,
  onShowDetails,
  detailsVisible,
  cardColor = 'white',
  nextBillingAmount,
  nextBillingDate,
  basePlanPrice,
  cancellationDate,
  expirationDate,
  layout = 'expandable-card',
  autoResumeDate,
  trialExpirationDate,
  priceAfterTrial,
  trialPrice,
  currentBalance,
  nextBillingAttempt,
  interval,
  billingDetails,
  renderComponentsSection,
  renderCustomFieldsSection,
  scheduledCancellationDate,
  availableActions,
  referralCode,
  threeDsActionLink,
  currency,
  coupons,
  subscriptionDetailsService,
}: SubscriptionProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const { modalsService } = useServices();
  const { device } = useDeviceInfo();
  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);
  const {
    subscription: { coupons: areCouponsDisabled },
  } = useComponentSettings();
  const showCoupons = coupons?.length > 0 || areCouponsDisabled;
  const isExpired = billingDetails?.creditCard?.expired;

  const isReactivateButtonDisabled =
    !billingDetails?.paymentProfileConnected || isExpired;

  const canReactivate = useMemo(() => {
    return (
      isReactivateButtonDisabled &&
      availableActions.includes(SubscriptionActions.REACTIVATE)
    );
  }, [isReactivateButtonDisabled, availableActions]);

  const showPauseSubscriptionModal = () => {
    modalsService.send({
      type: 'SHOW_PAUSE_SUBSCRIPTION',
      catalogId,
      subscriptionId: id,
    });
  };
  const showPlanChangeModal = () => {
    modalsService.send({
      type: 'SHOW_SUBSCRIPTION_PLAN_CHANGE',
      catalogId,
      subscriptionId: id,
    });
  };
  const show3DSModal = () => {
    modalsService.send({
      type: 'SHOW_THREE_D_SECURE',
      subscriptionId: id,
      catalogId,
      threeDsActionLink,
    });
  };

  return (
    <SubscriptionItem marginBottom={5} data-testid={`subscription-${id}`}>
      <Card color={cardColor}>
        <div
          onClick={onShowDetails}
          style={{
            cursor: layout === 'expandable-card' ? 'pointer' : 'auto',
          }}
        >
          <Card.Header enableBorder={detailsVisible}>
            <SubscriptionHeader
              title={name}
              price={price}
              interval={interval}
              tag={statusTag}
            />
          </Card.Header>
        </div>
        <AnimatePresence initial={layout !== 'all-details'}>
          {detailsVisible && (
            <motion.div {...bodyMotion} style={{ overflow: 'hidden' }}>
              <Card.Body>
                <div>
                  <Row>
                    <Column span={[24, 7]}>
                      <Heading variant="h4">
                        {t('subscription_details.plan_details', {
                          ns: 'subscriptions-manager',
                        })}
                      </Heading>
                    </Column>
                    <Column span={[24, 17]}>
                      <Notification
                        existingBalance={currentBalance}
                        status={status}
                        marginBottom={5}
                        show3DSModal={threeDsActionLink && show3DSModal}
                      />
                      <BodyText>{description}</BodyText>
                      <SubscriptionDetails
                        scheduledCancellationDate={scheduledCancellationDate}
                        subscriptionStatus={status}
                        nextBillingAmount={nextBillingAmount}
                        nextBillingDate={nextBillingDate}
                        basePlanPrice={basePlanPrice}
                        cancellationDate={cancellationDate}
                        expirationDate={expirationDate}
                        trialExpirationDate={trialExpirationDate}
                        priceAfterTrial={priceAfterTrial}
                        trialPrice={trialPrice}
                        nextBillingAttempt={nextBillingAttempt}
                        currentBalance={currentBalance}
                        autoResumeDate={autoResumeDate}
                      />
                      {referralCode ? (
                        <SubscriptionReferalCode
                          label={t('subscription_details.referral_code')}
                          referalCode={referralCode}
                        />
                      ) : null}
                      <Button.Group>
                        {availableActions.includes(
                          SubscriptionActions.CHANGE_PLAN
                        ) && (
                          <Button
                            color="primary"
                            size="medium"
                            label={t('plan_change.configure_plan')}
                            onClick={showPlanChangeModal}
                          />
                        )}
                        {availableActions.includes(
                          SubscriptionActions.PAUSE
                        ) && (
                          <Button
                            color="secondary"
                            size="medium"
                            label={t('pause_subscription.put_on_hold')}
                            onClick={showPauseSubscriptionModal}
                          />
                        )}
                        {availableActions.includes(
                          SubscriptionActions.RESUME
                        ) && (
                          <Button
                            label={t('resume_subscription.resume')}
                            onClick={() => {
                              modalsService.send({
                                type: 'SHOW_RESUME_SUBSCRIPTION',
                                subscriptionId: id,
                                catalogId,
                              });
                            }}
                          />
                        )}
                        {availableActions.includes(
                          SubscriptionActions.CANCEL
                        ) && (
                          <Button
                            color="secondary"
                            label={t('cancel_subscription.cancel')}
                            onClick={() => {
                              modalsService.send({
                                type: 'SHOW_CANCEL_SUBSCRIPTION',
                                subscriptionId: id,
                                catalogId,
                              });
                            }}
                          />
                        )}
                        {availableActions.includes(
                          SubscriptionActions.UNDO_CANCELATION
                        ) && (
                          <Button
                            color="secondary"
                            label={t(
                              'cancel_subscription.undo_cancelation_button'
                            )}
                            onClick={() => {
                              modalsService.send({
                                type: 'SHOW_UNDO_CANCELATION',
                                subscriptionId: id,
                                catalogId,
                              });
                            }}
                          />
                        )}
                        {availableActions.includes(
                          SubscriptionActions.REACTIVATE
                        ) && (
                          <Button
                            data-testid="reactivateButton"
                            color="primary"
                            label={t('reactivate_subscription.reactivate')}
                            onClick={() =>
                              modalsService.send({
                                type: 'SHOW_REACTIVATE_SUBSCRIPTION',
                                subscriptionId: id,
                                catalogId,
                              })
                            }
                            disabled={isReactivateButtonDisabled}
                          />
                        )}
                      </Button.Group>
                      {canReactivate && (
                        <Box marginTop={4} marginBottom={4}>
                          <Alert color="primary">
                            <Alert.TextMessage>
                              <Trans
                                t={t}
                                i18nKey="reactivate_subscription.update_payment_method"
                                components={{
                                  updatePaymentMethodLink: (
                                    <Link
                                      style={{
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        fontWeight: 700,
                                      }}
                                      to={`/paymentProfiles/${catalogId}/${id}`}
                                    ></Link>
                                  ),
                                }}
                              />
                            </Alert.TextMessage>
                          </Alert>
                        </Box>
                      )}
                    </Column>
                  </Row>
                </div>
                {renderComponentsSection && (
                  <ComponentsContainer
                    catalogId={catalogId}
                    subscriptionId={id}
                  />
                )}
                <RowWrapper marginTop={5} paddingTop={5}>
                  <Row>
                    <Column span={[24, 7]}>
                      <Heading variant="h4">
                        {t('subscription_details.billing_info', {
                          ns: 'subscriptions-manager',
                        })}
                      </Heading>
                    </Column>
                    <Column span={[24, 17]}>
                      <BillingDetailsContainer
                        subscriptionId={id}
                        catalogId={catalogId}
                        billingDetails={billingDetails}
                        isExpired={isExpired}
                      />
                    </Column>
                  </Row>
                </RowWrapper>
                {renderCustomFieldsSection && (
                  <RowWrapper marginTop={5} paddingTop={5}>
                    <CustomFieldsContainer
                      subscriptionId={id}
                      catalogId={catalogId}
                    />
                  </RowWrapper>
                )}
                {showCoupons && (
                  <RowWrapper marginTop={5} paddingTop={5}>
                    <CouponsContainer
                      subscriptionId={id}
                      catalogId={catalogId}
                      currency={currency}
                      subscriptionDetailsService={subscriptionDetailsService}
                    />
                  </RowWrapper>
                )}
              </Card.Body>
            </motion.div>
          )}
        </AnimatePresence>
        {layout === 'expandable-card' && detailsVisible && isOnMobileDevice && (
          <Card.Footer>
            <FooterContentWrapper>
              {detailsVisible && (
                <ToggleWrapper marginTop={3} onClick={onShowDetails}>
                  <Anchor label={t('less', { ns: 'common' })} />
                </ToggleWrapper>
              )}
            </FooterContentWrapper>
          </Card.Footer>
        )}
      </Card>
    </SubscriptionItem>
  );
};

export default Subscription;
